import { useEffect, useRef, useState } from "react";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WashingMachine, Fan, Coffee, Gear } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/**
 * Kompakte Inline-Counter-Leiste: 3 quadratische Icon-Buttons + Zahnrad (nur Admin).
 * Wird direkt neben „Dienst starten" auf dem Dashboard platziert.
 */
export default function WashCounter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);

  const load = () => api.get("/wash-counter/stats").then(r => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const click = async (device) => {
    setBusy(device);
    try {
      const r = await api.post("/wash-counter/click", { device });
      const labels = { wash: "Waschmaschine", dryer: "Trockner", espresso: "Espresso" };
      const c = r.data.cost || 0;
      toast.success(c > 0 ? `${labels[device]} +1 · ${euro(c)} verbucht` : `${labels[device]} +1 (kein Betrag hinterlegt)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(null); }
  };

  const undo = async (device) => {
    setBusy(device);
    try {
      const r = await api.post("/wash-counter/undo", { device });
      const labels = { wash: "Waschmaschine", dryer: "Trockner", espresso: "Espresso" };
      const c = r.data.cost || 0;
      toast.success(c > 0 ? `${labels[device]} −1 · ${euro(c)} storniert` : `${labels[device]} −1 · Klick zurückgenommen`, { duration: 2500 });
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Fehler";
      if (e.response?.status === 404) toast.info(msg);
      else toast.error(msg);
    } finally { setBusy(null); }
  };

  const t = stats?.today || {};
  const cfg = stats?.config || {};

  return (
    <div className="flex items-center gap-1.5" data-testid="wash-counter">
      <CounterButton
        testid="btn-wash-start"
        icon={WashingMachine}
        color="border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
        badgeColor="bg-sky-600"
        title={`Waschmaschine +1${cfg.wash_cost ? ` (${euro(cfg.wash_cost)})` : ""} · Rechtsklick / lang drücken = −1`}
        count={t.wash?.count || 0}
        busy={busy === "wash"}
        onClick={() => click("wash")}
        onUndo={() => undo("wash")}
      />
      <CounterButton
        testid="btn-dryer-start"
        icon={Fan}
        color="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
        badgeColor="bg-amber-600"
        title={`Trockner +1${cfg.dryer_cost ? ` (${euro(cfg.dryer_cost)})` : ""} · Rechtsklick / lang drücken = −1`}
        count={t.dryer?.count || 0}
        busy={busy === "dryer"}
        onClick={() => click("dryer")}
        onUndo={() => undo("dryer")}
      />
      <CounterButton
        testid="btn-espresso-start"
        icon={Coffee}
        color="border-stone-300 bg-stone-100 text-stone-700 hover:bg-stone-200"
        badgeColor="bg-stone-700"
        title={`Espresso +1${cfg.espresso_cost ? ` (${euro(cfg.espresso_cost)})` : ""} · Rechtsklick / lang drücken = −1`}
        count={t.espresso?.count || 0}
        busy={busy === "espresso"}
        onClick={() => click("espresso")}
        onUndo={() => undo("espresso")}
      />
      {isAdmin && (
        <button
          onClick={() => setCfgOpen(true)}
          className="ml-0.5 h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 flex items-center justify-center transition"
          title="Kosten pro Ladung/Espresso einstellen"
          data-testid="wash-config-btn">
          <Gear size={14} weight="bold"/>
        </button>
      )}
      {cfgOpen && (
        <ConfigDialog value={cfg} onClose={() => setCfgOpen(false)} onSaved={() => { setCfgOpen(false); load(); }}/>
      )}
    </div>
  );
}

function CounterButton({ testid, icon: Icon, color, badgeColor, title, count, busy, onClick, onUndo }) {
  const timerRef = useRef(null);
  const longPressed = useRef(false);

  const startHold = () => {
    longPressed.current = false;
    timerRef.current = setTimeout(() => {
      longPressed.current = true;
      onUndo && onUndo();
    }, 550);
  };
  const cancelHold = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  const handleClick = (e) => {
    if (longPressed.current) { longPressed.current = false; return; }
    onClick && onClick();
  };
  const handleContextMenu = (e) => {
    e.preventDefault();
    cancelHold();
    onUndo && onUndo();
  };

  return (
    <button
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={startHold}
      onMouseUp={cancelHold}
      onMouseLeave={cancelHold}
      onTouchStart={startHold}
      onTouchEnd={cancelHold}
      onTouchCancel={cancelHold}
      disabled={busy}
      title={title}
      data-testid={testid}
      className={`h-9 w-9 rounded-lg border flex items-center justify-center transition disabled:opacity-60 relative select-none ${color}`}
      style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}>
      <Icon size={16} weight="duotone"/>
      {count > 0 && (
        <span className={`absolute -top-1.5 -right-1.5 ${badgeColor} text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-0.5 flex items-center justify-center leading-none`}>
          {count}
        </span>
      )}
    </button>
  );
}

function ConfigDialog({ value, onClose, onSaved }) {
  const [w, setW] = useState(value.wash_cost || 0);
  const [d, setD] = useState(value.dryer_cost || 0);
  const [e, setE] = useState(value.espresso_cost ?? 0.20);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/wash-counter/config", {
        wash_cost: parseFloat(w) || 0,
        dryer_cost: parseFloat(d) || 0,
        espresso_cost: parseFloat(e) || 0,
      });
      toast.success("Kosten gespeichert");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="wash-config-dialog">
        <DialogHeader><DialogTitle>Kosten pro Ladung / Espresso</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Waschmaschine (EUR)</Label>
            <Input type="number" step="0.01" min="0" value={w} onChange={(ev)=>setW(ev.target.value)} className="h-11 rounded-lg mt-1" data-testid="cfg-wash-cost"/>
          </div>
          <div>
            <Label className="text-xs">Trockner (EUR)</Label>
            <Input type="number" step="0.01" min="0" value={d} onChange={(ev)=>setD(ev.target.value)} className="h-11 rounded-lg mt-1" data-testid="cfg-dryer-cost"/>
          </div>
          <div>
            <Label className="text-xs">Espresso (EUR)</Label>
            <Input type="number" step="0.01" min="0" value={e} onChange={(ev)=>setE(ev.target.value)} className="h-11 rounded-lg mt-1" data-testid="cfg-espresso-cost"/>
          </div>
          <p className="text-[11px] text-muted-foreground">Ab dem Zeitpunkt des Speicherns wird bei jedem Klick automatisch eine Ausgabe in der jeweiligen Kategorie erstellt.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-lg">Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="rounded-lg bg-primary text-white" data-testid="cfg-save">Speichern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
